import { useMemo, useRef, useState } from "react";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Mail, Camera, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fileToResizedDataUrl } from "@/lib/image";
import { isHiddenUser, isSuperAdmin } from "@/lib/roles";

const COLORS = ["#0A84FF", "#FF375F", "#30D158", "#FF9F0A", "#BF5AF2", "#5AC8FA", "#FFD60A"];

function UserAvatarUpload({ user, canEdit }) {
  const { updateUser } = useData();
  const { refresh } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Immagine troppo grande (max 4MB)");
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 320, 0.85);
      await updateUser(user.id, { avatar_url: dataUrl });
      // Refresh /auth/me if updating self so header avatar updates too
      await refresh();
      toast.success("Foto profilo aggiornata");
    } catch {
      toast.error("Errore caricamento");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => canEdit && inputRef.current?.click()}
        data-testid={`user-avatar-${user.id}`}
        className={`relative h-12 w-12 rounded-full overflow-hidden shrink-0 focus:outline-none ${canEdit ? "focus:ring-2 focus:ring-fuchsia-500/60 group" : "cursor-default"}`}
        title={canEdit ? "Cambia foto" : ""}
        disabled={!canEdit}
      >
        <UserAvatar user={user} size={48} />
        {canEdit && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-colors">
            <Camera size={16} className="text-white" />
          </span>
        )}
        {uploading && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
            <span className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          </span>
        )}
      </button>
      {canEdit && (
        <input
          ref={inputRef}
          data-testid={`user-avatar-input-${user.id}`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
        />
      )}
    </>
  );
}

export default function Team() {
  const { users, clients, addUser, deleteUser, updateUser, tasks } = useData();
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", color: COLORS[0] });
  const [saving, setSaving] = useState(false);

  // Modal + form per il nuovo utente CLIENTE
  const [clientOpen, setClientOpen] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    password: "",
    color: COLORS[0],
    client_scope: [],
  });
  const [savingClient, setSavingClient] = useState(false);

  const isAdmin = me?.role === "admin";
  const iAmSuper = isSuperAdmin(me);

  // Team members list: exclude cliente-role users and the super-admin (hidden everywhere)
  const visibleUsers = useMemo(() => {
    return users.filter((u) => {
      if (isHiddenUser(u)) return false;
      if (u.role === "cliente") return false;
      return true;
    });
  }, [users]);

  const clienteUsers = useMemo(() => users.filter((u) => u.role === "cliente"), [users]);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, name: "", email: "", password: "", client_scope: [] });
  const openEditCliente = (u) => {
    setEditForm({ id: u.id, name: u.name, email: u.email, password: "", client_scope: u.client_scope || [] });
    setEditOpen(true);
  };
  const toggleEditScope = (id) =>
    setEditForm((f) => ({
      ...f,
      client_scope: f.client_scope.includes(id) ? f.client_scope.filter((x) => x !== id) : [...f.client_scope, id],
    }));
  const saveEditCliente = async () => {
    if (!editForm.email.trim()) return toast.error("Email obbligatoria");
    if (editForm.password && editForm.password.length < 6) return toast.error("Password: minimo 6 caratteri");
    try {
      const patch = { name: editForm.name, email: editForm.email.trim(), client_scope: editForm.client_scope };
      if (editForm.password) patch.password = editForm.password;
      await updateUser(editForm.id, patch);
      toast.success("Accesso esterno aggiornato");
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const clientById = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.id] = c;
    return m;
  }, [clients]);

  const submitCliente = async () => {
    if (!clientForm.name.trim() || !clientForm.email.trim())
      return toast.error("Nome ed email richiesti");
    if (!clientForm.password || clientForm.password.length < 6)
      return toast.error("Password minimo 6 caratteri");
    if (!clientForm.client_scope || clientForm.client_scope.length === 0)
      return toast.error("Seleziona almeno un cliente dal menu");
    setSavingClient(true);
    try {
      await addUser({
        name: clientForm.name,
        email: clientForm.email,
        color: clientForm.color,
        role: "cliente",
        password: clientForm.password,
        client_scope: clientForm.client_scope,
      });
      toast.success("Utente cliente creato");
      setClientOpen(false);
      setClientForm({ name: "", email: "", password: "", color: COLORS[0], client_scope: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSavingClient(false);
    }
  };
  const toggleScope = (id) =>
    setClientForm((f) => ({
      ...f,
      client_scope: f.client_scope.includes(id)
        ? f.client_scope.filter((x) => x !== id)
        : [...f.client_scope, id],
    }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Nome ed email richiesti");
    setSaving(true);
    try {
      await addUser(form);
      toast.success("Utente aggiunto");
      setOpen(false);
      setForm({ name: "", email: "", color: COLORS[0] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me.id) return toast.error("Non puoi eliminare te stesso");
    if (!confirm(`Eliminare ${u.name}?`)) return;
    await deleteUser(u.id);
    toast.success("Utente eliminato");
  };

  const toggleAdmin = async (u, next) => {
    try {
      await updateUser(u.id, { role: next ? "admin" : "member" });
      toast.success(next ? `${u.name} promosso ad admin` : `${u.name} è ora un membro`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Persone</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1">
            Team{" "}
            <span className="text-zinc-500 font-normal tabular-nums" data-testid="team-count">
              ({visibleUsers.length})
            </span>
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Membri del team e loro carico di lavoro attivo.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-user-button"
                className="tp-brand-btn h-10 rounded-full"
              >
                <Plus size={14} className="mr-1.5" /> Aggiungi membro
              </Button>
            </DialogTrigger>
            <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">Nuovo membro del team</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Nome</Label>
                  <Input
                    data-testid="user-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-white/5 border-white/10 text-white h-11"
                    placeholder="Mario Rossi"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                  <Input
                    data-testid="user-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white/5 border-white/10 text-white h-11"
                    placeholder="mario@company.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Colore avatar</Label>
                  <div className="flex gap-2">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setForm({ ...form, color: c })}
                        className={`h-8 w-8 rounded-full border-2 transition-transform ${
                          form.color === c ? "border-white scale-110" : "border-transparent"
                        }`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-zinc-500 bg-white/5 border border-white/10 rounded-lg p-2">
                  Password iniziale: <span className="text-zinc-300 font-medium">welcome123</span>
                </div>
                <Button
                  data-testid="user-save-button"
                  onClick={submit}
                  disabled={saving}
                  className="tp-brand-btn w-full rounded-xl"
                >
                  {saving ? "Salvo..." : "Crea utente"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {visibleUsers.length === 0 ? (
        <div className="tp-glass rounded-3xl p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            <ShieldCheck className="text-zinc-400" />
          </div>
          <h3 className="text-xl font-semibold text-white">Nessun membro nel team</h3>
          <p className="text-sm text-zinc-400 mt-1">
            {isAdmin ? "Aggiungi il primo membro." : "Chiedi a un admin di aggiungerti al team."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleUsers.map((u) => {
            const active = tasks.filter(
              (t) => t.assignee_ids?.includes(u.id) && t.status !== "completato",
            ).length;
            const now = Date.now();
            const monthAgo = now - 30 * 24 * 60 * 60 * 1000;
            const lastMonthCompleted = tasks.filter((t) => {
              if (!t.assignee_ids?.includes(u.id)) return false;
              if (t.status !== "completato") return false;
              const ts = t.updated_at ? Date.parse(t.updated_at) : 0;
              return ts >= monthAgo && ts <= now;
            }).length;
            const canEditAvatar = isAdmin || u.id === me.id;
            return (
              <div
                key={u.id}
                data-testid={`user-card-${u.id}`}
                className="tp-glass rounded-2xl p-5 tp-hover-lift flex items-center gap-4"
              >
                <UserAvatarUpload user={u} canEdit={canEditAvatar} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-white truncate">{u.name}</div>
                    {u.role === "admin" && (
                      <span
                        data-testid={`user-admin-badge-${u.id}`}
                        className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30"
                      >
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1 truncate">
                    <Mail size={11} /> {u.email}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1.5">
                    <span className="text-white font-medium tabular-nums">{active}</span> task attive
                  </div>
                  <div
                    className="text-[11px] text-zinc-500 mt-0.5"
                    data-testid={`user-monthly-count-${u.id}`}
                  >
                    <span className="text-zinc-300 tabular-nums">{lastMonthCompleted}</span> Task
                    nell&apos;ultimo mese
                  </div>
                  {iAmSuper && u.id !== me.id && (
                    <div className="mt-2 flex items-center gap-2">
                      <Switch
                        checked={u.role === "admin"}
                        onCheckedChange={(v) => toggleAdmin(u, v)}
                        data-testid={`user-admin-toggle-${u.id}`}
                      />
                      <span className="text-[11px] uppercase tracking-wider text-zinc-400">
                        Admin
                      </span>
                    </div>
                  )}
                </div>
                {isAdmin && u.id !== me.id && (u.role !== "admin" || iAmSuper) && (
                  <button
                    onClick={() => remove(u)}
                    data-testid={`delete-user-${u.id}`}
                    className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sezione TASK CLIENTI - utenti con ruolo "cliente" */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-400/80">
              Accessi esterni
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white mt-1">
              Task Clienti{" "}
              <span className="text-zinc-500 font-normal tabular-nums">({clienteUsers.length})</span>
            </h2>
            <p className="text-sm text-zinc-400 mt-1">
              Account cliente: vedono solo &quot;Il mio lavoro&quot; con i clienti assegnati.
            </p>
          </div>
          {isAdmin && (
            <Dialog open={clientOpen} onOpenChange={setClientOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-cliente-button" className="tp-brand-btn h-10 rounded-full">
                  <Plus size={14} className="mr-1.5" /> Aggiungi Cliente
                </Button>
              </DialogTrigger>
              <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Nuovo utente cliente</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Nome</Label>
                    <Input
                      data-testid="cliente-name-input"
                      value={clientForm.name}
                      onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                    <Input
                      data-testid="cliente-email-input"
                      value={clientForm.email}
                      onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Password iniziale
                    </Label>
                    <Input
                      data-testid="cliente-password-input"
                      type="password"
                      value={clientForm.password}
                      onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-11"
                      placeholder="Almeno 6 caratteri"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Colore avatar
                    </Label>
                    <div className="flex gap-2">
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          onClick={() => setClientForm({ ...clientForm, color: c })}
                          className={`h-8 w-8 rounded-full border-2 transition-transform ${
                            clientForm.color === c ? "border-white scale-110" : "border-transparent"
                          }`}
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-zinc-400">
                      Clienti visibili · seleziona uno o più
                    </Label>
                    <div className="max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] p-2 space-y-1">
                      {clients.length === 0 && (
                        <div className="text-xs text-zinc-500 p-2">Nessun cliente disponibile</div>
                      )}
                      {clients.map((c) => {
                        const on = clientForm.client_scope.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleScope(c.id)}
                            data-testid={`cliente-scope-toggle-${c.id}`}
                            className={`w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                              on
                                ? "bg-fuchsia-500/20 border border-fuchsia-400/40 text-white"
                                : "bg-white/[0.02] border border-white/5 text-zinc-300 hover:bg-white/10"
                            }`}
                          >
                            <span
                              className="h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ background: c.color }}
                            />
                            <span className="flex-1 truncate">{c.name}</span>
                            {on && <span className="text-fuchsia-300 text-xs">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {clientForm.client_scope.length} selezionati
                    </div>
                  </div>
                  <Button
                    data-testid="cliente-save-button"
                    onClick={submitCliente}
                    disabled={savingClient}
                    className="tp-brand-btn w-full rounded-xl"
                  >
                    {savingClient ? "Salvo..." : "Crea utente cliente"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {clienteUsers.length === 0 ? (
          <div className="tp-glass rounded-3xl p-8 text-center">
            <div className="text-sm text-zinc-400">
              Nessun utente cliente. Usa il pulsante &quot;Aggiungi Cliente&quot; per crearne uno.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {clienteUsers.map((u) => (
              <div
                key={u.id}
                data-testid={`cliente-card-${u.id}`}
                className="tp-glass rounded-2xl p-5 tp-hover-lift flex items-start gap-4"
              >
                <UserAvatarUpload user={u} canEdit={isAdmin} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">{u.name}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1 truncate">
                    <Mail size={11} /> {u.email}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(u.client_scope || []).map((cid) => {
                      const c = clientById[cid];
                      if (!c) return null;
                      return (
                        <span
                          key={cid}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-zinc-300"
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ background: c.color }}
                          />
                          {c.name}
                        </span>
                      );
                    })}
                    {(u.client_scope || []).length === 0 && (
                      <span className="text-[10px] text-zinc-500">Nessun cliente assegnato</span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => openEditCliente(u)}
                      data-testid={`edit-cliente-${u.id}`}
                      title="Gestisci accesso"
                      className="p-2 rounded-lg text-zinc-500 hover:text-fuchsia-300 hover:bg-white/5 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      data-testid={`delete-cliente-${u.id}`}
                      className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gestione accesso esterno (cliente) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md" data-testid="edit-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Gestisci accesso esterno</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-400">Nome</label>
              <Input data-testid="edit-cliente-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">Email (username di accesso)</label>
              <Input data-testid="edit-cliente-email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">Nuova password (lascia vuoto per non cambiarla)</label>
              <Input data-testid="edit-cliente-password" type="text" value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••" className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">Clienti visibili</label>
              <div className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] p-2 space-y-1">
                {clients.filter((c) => c.id !== "admin-section").map((c) => {
                  const on = editForm.client_scope.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleEditScope(c.id)} data-testid={`edit-scope-toggle-${c.id}`}
                      className={`w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${on ? "bg-fuchsia-500/20 border border-fuchsia-400/40 text-white" : "bg-white/[0.02] border border-white/5 text-zinc-300 hover:bg-white/10"}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="flex-1 truncate">{c.name}</span>
                      {on && <span className="text-fuchsia-300 text-xs">✓</span>}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">{editForm.client_scope.length} selezionati</div>
            </div>
            <Button data-testid="edit-cliente-save" onClick={saveEditCliente} className="tp-brand-btn w-full rounded-xl">Salva accesso</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
